import { PHYSICS_DT } from '../constants';

export type PhysicsUpdateFn = (dt: number) => void;
export type RenderFn = (alpha: number) => void;

/**
 * Fixed-timestep game loop with render interpolation.
 * Physics runs at 240Hz regardless of display refresh rate.
 */
export class GameLoop {
  private accumulator = 0;
  private timeScale = 1.0;
  private physicsUpdate: PhysicsUpdateFn | null = null;
  private render: RenderFn | null = null;
  private lastTime = 0;
  private running = false;

  setPhysicsUpdate(fn: PhysicsUpdateFn): void {
    this.physicsUpdate = fn;
  }

  setRender(fn: RenderFn): void {
    this.render = fn;
  }

  setTimeScale(scale: number): void {
    this.timeScale = Math.max(0, Math.min(scale, 5));
  }

  getTimeScale(): number {
    return this.timeScale;
  }

  start(): void {
    this.running = true;
    this.lastTime = performance.now() / 1000;
    this.accumulator = 0;
  }

  stop(): void {
    this.running = false;
  }

  /**
   * Called each frame by Babylon's render loop.
   * Runs fixed physics steps, then renders with interpolation alpha.
   */
  tick(): void {
    if (!this.running) return;

    const now = performance.now() / 1000;
    let frameDelta = now - this.lastTime;
    this.lastTime = now;

    // Clamp large deltas (tab switch, etc)
    if (frameDelta > 0.1) frameDelta = 0.1;

    this.accumulator += frameDelta * this.timeScale;

    // Fixed physics steps
    while (this.accumulator >= PHYSICS_DT) {
      this.physicsUpdate?.(PHYSICS_DT);
      this.accumulator -= PHYSICS_DT;
    }

    // Render interpolation
    const alpha = this.accumulator / PHYSICS_DT;
    this.render?.(alpha);
  }
}
